import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  CalendarClock,
  CreditCard,
  Edit3,
  FileText,
  Globe,
  Mail,
  MapPin,
  Phone,
  Plus,
  ScrollText,
  Search,
  User,
  Users,
  Wallet
} from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { createFinanceClient, listFinanceClients, updateFinanceClient } from '../../../../../services/ClientsService';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { usePaymentConditions } from '../../../../../hooks/usePaymentConditions';
import {
  FinanceClient,
  PRESET_CLIENTS,
  STORAGE_KEYS,
  formatEuro,
  fullAddress,
  nextCode
} from './financeMasters';
import { useStoredList } from './useStoredList';
import { AddressFormSection, AttachmentsCard, AttachmentsFormSection } from './MasterParts';
import { FinanceAccountItems } from './FinanceAccountItems';
import '../../shared-modules.css';
import './FinanceMasters.css';

type ClientForm = Omit<FinanceClient, 'id' | 'balance'>;

const EMPTY_FORM: ClientForm = {
  name: '',
  contactPerson: '',
  email: '',
  cif: '',
  phone: '',
  street: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'España',
  account: '430000',
  paymentTerms: 'Neto 30 días',
  preferredPaymentMethod: 'Transferencia bancaria',
  billingLanguage: 'Español',
  notes: '',
  attachments: [],
  status: 'Activo'
};

const LANGUAGES = ['Español', 'Catalán', 'Gallego', 'Euskera', 'Inglés', 'Francés'];

interface FinanceClientsViewProps {
  privacyMode: boolean;
}

export const FinanceClientsView = ({ privacyMode }: FinanceClientsViewProps) => {
  const { notify } = useToast();

  const [clients, setClients] = useStoredList<FinanceClient>(STORAGE_KEYS.clients, PRESET_CLIENTS);
  const { conditions } = usePaymentConditions();
  const { methods } = usePaymentMethods();

  // Clientes reales del backend; si /client-list falla se mantiene la lista local
  useEffect(() => {
    let cancelled = false;
    listFinanceClients()
      .then(list => { if (!cancelled && list.length > 0) setClients(list); })
      .catch(err => console.error('Error fetching clients:', err));
    return () => { cancelled = true; };
  }, []);

  const [selectedClient, setSelectedClient] = useState<FinanceClient | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ClientForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Partidas del deudor, a pantalla completa
  const [itemsClient, setItemsClient] = useState<FinanceClient | null>(null);

  const setField = <K extends keyof ClientForm>(field: K, value: ClientForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreateNewClick = () => {
    setSelectedClient(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (client: FinanceClient) => {
    const { id, balance, ...rest } = client;
    setSelectedClient(client);
    setFormData({ ...EMPTY_FORM, ...rest, attachments: [...(client.attachments || [])] });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectClient = (client: FinanceClient) => {
    setIsFormOpen(false);
    setSelectedClient(client);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedClient(null);
  };

  const openItems = (client: FinanceClient) => {
    closePanel();
    setItemsClient(client);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedClient) && !isFormOpen);

  const handleSaveClient = async () => {
    if (!formData.name.trim() || !formData.cif.trim()) {
      setFormError('La razón social y el CIF son obligatorios.');
      return;
    }
    const data: ClientForm = {
      ...formData,
      name: formData.name.trim(),
      cif: formData.cif.toUpperCase().trim()
    };
    const payload = { ...data, address: fullAddress(data) };
    const termsDays = conditions.find(c => c.name === data.paymentTerms)?.days ?? 30;

    setSaving(true);
    setFormError('');
    try {
      if (isEditing && selectedClient) {
        const record = await updateFinanceClient(selectedClient.id, payload, termsDays);
        // Lo que el backend no guarda se conserva en pantalla hasta recargar
        const updated: FinanceClient = { ...selectedClient, ...data, id: record.id };
        setClients(prev => prev.map(c => (c.id === updated.id ? updated : c)));
        setSelectedClient(updated);
        notify('Éxito', `Cliente "${updated.name}" modificado correctamente.`, 'info');
      } else {
        const record = await createFinanceClient(payload, termsDays);
        const created: FinanceClient = { ...data, id: record.id, balance: 0 };
        setClients(prev => [created, ...prev]);
        setSelectedClient(created);
        notify('Éxito', `Cliente "${created.name}" dado de alta.`, 'info');
      }
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving client:', err);
      setFormError(err instanceof Error ? err.message : 'No se pudo guardar el cliente.');
    } finally {
      setSaving(false);
    }
  };

  const query = searchQuery.toLowerCase();
  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(query) ||
    c.cif.toLowerCase().includes(query) ||
    c.contactPerson.toLowerCase().includes(query)
  );

  const columns: ColumnDef<FinanceClient>[] = [
    {
      id: 'id',
      header: 'CÓDIGO',
      render: (c) => <span className="fin-master__code">{c.id}</span>
    },
    {
      id: 'name',
      header: 'RAZÓN SOCIAL / CIF',
      render: (c) => (
        <>
          <div className="fin-master__name">{c.name}</div>
          <div className="fin-master__sub">CIF: {c.cif}</div>
        </>
      )
    },
    {
      id: 'contact',
      header: 'CONTACTO',
      render: (c) => (
        <>
          <div className="fin-master__text">{c.contactPerson || '—'}</div>
          <div className="fin-master__sub fin-master__sub--lower">{c.email || '—'}</div>
        </>
      )
    },
    {
      id: 'account',
      header: 'CUENTA PGC',
      render: (c) => <span className="fin-master__chip">{c.account}</span>
    },
    {
      id: 'paymentTerms',
      header: 'CONDICIONES DE PAGO',
      render: (c) => (
        <>
          <div className="fin-master__text">{c.paymentTerms}</div>
          <div className="fin-master__sub">{c.preferredPaymentMethod}</div>
        </>
      )
    },
    {
      id: 'balance',
      header: 'SALDO PENDIENTE',
      align: 'right',
      render: (c) => (
        <span className={`fin-master__amount ${c.balance > 0 ? 'pending' : ''}`}>{formatEuro(c.balance, privacyMode)}</span>
      )
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (c) => (
        <span className={`module-badge ${c.status === 'Activo' ? 'active' : 'inactive'}`}>{c.status}</span>
      )
    }
  ];

  // Mientras están abiertas sustituyen al maestro
  if (itemsClient) {
    return <FinanceAccountItems kind="deudor" account={itemsClient} privacyMode={privacyMode} onClose={() => setItemsClient(null)} />;
  }

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR RAZÓN SOCIAL, CIF O PERSONA DE CONTACTO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nuevo_Cliente
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedClient || isFormOpen)}>
        <DataTable
          data={filteredClients}
          columns={columns}
          title={<><Users size={14} className="module-panel-title-icon" /> Maestro_De_Clientes</>}
          subtitle={`TOTAL: ${filteredClients.length}`}
          getRowId={(c) => c.id}
          onRowClick={handleSelectClient}
          selectedRowId={selectedClient?.id}
          emptyText="No se encontraron clientes."
          actionsHeader="ACCIONES"
          actionsColumn={(c) => (
            <RowActionsMenu
              actions={[
                viewAction(() => handleSelectClient(c)),
                editAction(() => handleEditClick(c)),
                { label: 'Ver partidas', icon: ScrollText, tone: 'accent', onSelect: () => openItems(c) }
              ]}
            />
          )}
        />

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Ficha_Cliente' : 'Alta_De_Nuevo_Cliente'}
            onClose={closePanel}
            submitLabel={saving ? 'Guardando...' : 'Guardar_Cliente'}
            submitDisabled={saving}
            onSubmit={handleSaveClient}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_Identificativos" icon={FileText}>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Razón Social / Nombre Comercial *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Ej. Librería Central Madrid S.L."
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">CIF / NIF *</label>
                  <input
                    type="text"
                    required
                    value={formData.cif}
                    onChange={(e) => setField('cif', e.target.value.toUpperCase())}
                    placeholder="Ej. B81234567"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setField('status', e.target.value as FinanceClient['status'])}
                    className="sidebar-form-input"
                  >
                    <option value="Activo">ACTIVO</option>
                    <option value="Inactivo">INACTIVO</option>
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Persona de Contacto</label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setField('contactPerson', e.target.value)}
                  placeholder="Ej. Carlos Gómez"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Email de Facturación</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="facturas@cliente.es"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Teléfono</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="+34 91 000 0000"
                    className="sidebar-form-input"
                  />
                </div>
              </div>
            </DetailSection>

            <AddressFormSection
              title="2. Dirección_Fiscal"
              address={formData}
              onChange={(field, value) => setField(field, value)}
            />

            <DetailSection title="3. Contabilidad_Y_Facturación" icon={BookOpen}>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta Contable PGC *</label>
                  <select
                    value={formData.account}
                    onChange={(e) => setField('account', e.target.value)}
                    className="sidebar-form-input"
                  >
                    <option value="430000">430000 - Clientes (Euros)</option>
                    <option value="431000">431000 - Efectos en cartera</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Condiciones de Pago *</label>
                  <select
                    value={formData.paymentTerms}
                    onChange={(e) => setField('paymentTerms', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {conditions.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Forma de Pago Preferida</label>
                  <select
                    value={formData.preferredPaymentMethod}
                    onChange={(e) => setField('preferredPaymentMethod', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {methods.map(m => (
                      <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Idioma de Facturación</label>
                  <select
                    value={formData.billingLanguage}
                    onChange={(e) => setField('billingLanguage', e.target.value)}
                    className="sidebar-form-input"
                  >
                    {LANGUAGES.map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Notas y Observaciones</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="Instrucciones de entrega, acuerdos comerciales, descuentos especiales..."
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>

            <AttachmentsFormSection
              title="4. Documentos_Adjuntos"
              attachments={formData.attachments}
              onChange={(attachments) => setField('attachments', attachments)}
            />
          </SidebarForm>
        ) : selectedClient ? (
          <DetailPanel
            expandable
            title="Ficha_De_Cliente"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedClient)} />
            }
            cards={<AttachmentsCard attachments={selectedClient.attachments} />}
          >
            <DetailIdentity
              icon={Users}
              reference={selectedClient.id}
              name={selectedClient.name}
              subtitle={
                <span className={`module-badge ${selectedClient.status === 'Activo' ? 'active' : 'inactive'}`}>
                  {selectedClient.status}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={FileText} label="CIF_FACTURACIÓN">{selectedClient.cif}</DetailField>
              <DetailField icon={MapPin} label="DIRECCIÓN_FISCAL">{fullAddress(selectedClient) || 'No declarada'}</DetailField>
              <DetailField icon={User} label="PERSONA_DE_CONTACTO">{selectedClient.contactPerson || 'No declarada'}</DetailField>
              <DetailField icon={Mail} label="EMAIL_DE_FACTURACIÓN">
                <span className="detail-field-value lowercase">{selectedClient.email || 'No declarado'}</span>
              </DetailField>
              <DetailField icon={Phone} label="TELÉFONO">{selectedClient.phone || 'No declarado'}</DetailField>
              <DetailField icon={BookOpen} label="CUENTA_PGC">{selectedClient.account}</DetailField>
              <DetailField icon={CalendarClock} label="CONDICIONES_DE_PAGO">{selectedClient.paymentTerms}</DetailField>
              <DetailField icon={CreditCard} label="FORMA_DE_PAGO">{selectedClient.preferredPaymentMethod || 'No declarada'}</DetailField>
              <DetailField icon={Globe} label="IDIOMA_DE_FACTURACIÓN">{selectedClient.billingLanguage}</DetailField>
              <DetailField icon={Wallet} label="SALDO_PENDIENTE">{formatEuro(selectedClient.balance, privacyMode)}</DetailField>
            </DetailFieldList>

            {selectedClient.notes && <DetailNotes>{selectedClient.notes}</DetailNotes>}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceClientsView;
